import { useState, useEffect } from 'react';
import { Eye, Mail, MousePointer, Clock, FileText, CreditCard, Send } from 'lucide-react';

export function LeadActivityPanel({ api, leadId, quotes }) {
  const [tracking, setTracking] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!quotes || quotes.length === 0) { setLoading(false); return; }
    var promises = quotes.map(function(q) {
      return api.get('/api/email-tracking/' + q.id).then(function(res) { return { quoteId: q.id, quoteName: q.name, data: res.data }; }).catch(function() { return { quoteId: q.id, quoteName: q.name, data: null }; });
    });
    Promise.all(promises).then(function(results) {
      var map = {};
      results.forEach(function(r) { map[r.quoteId] = r; });
      setTracking(map);
    }).finally(function() { setLoading(false); });
  }, [quotes]);

  var fmtDate = function(d) {
    if (!d) return '-';
    return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-[#014DB7] border-t-transparent rounded-full animate-spin" /></div>;

  if (!quotes || quotes.length === 0) return (
    <div className="text-center py-12 text-gray-500">
      <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
      <p>No quotes sent yet — activity will appear here after sending a quote</p>
    </div>
  );

  return (
    <div className="space-y-4">
      {quotes.map(function(q) {
        var t = tracking[q.id];
        return <QuoteActivityCard key={q.id} quote={q} trackingData={t} fmtDate={fmtDate} />;
      })}
    </div>
  );
}

function QuoteActivityCard({ quote, trackingData, fmtDate }) {
  var log = trackingData && trackingData.data && trackingData.data.email_logs ? trackingData.data.email_logs[0] : null;
  var totalViews = trackingData && trackingData.data ? trackingData.data.total_views || 0 : 0;
  var views = trackingData && trackingData.data ? trackingData.data.views || [] : [];

  var emailSent = log && log.sent_at;
  var emailOpens = log ? (log.opens || 0) : 0;
  var linkClicks = log ? (log.views || 0) : totalViews;
  var isSigned = quote.signed_at;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h4 className="font-semibold text-[#0A1628]">{quote.name || 'Quote'}</h4>
          <p className="text-xs text-gray-500">${(quote.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
        </div>
        {isSigned ? (
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">Signed</span>
        ) : emailSent ? (
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">Sent</span>
        ) : (
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">Draft</span>
        )}
      </div>

      {/* Timeline */}
      <div className="space-y-0">
        <TimelineRow icon={Send} label="Email Sent" time={emailSent ? fmtDate(log.sent_at) : null} active={!!emailSent} />
        <TimelineRow icon={Mail} label="Email Opened" time={emailOpens > 0 ? (emailOpens + 'x — Last: ' + fmtDate(log.last_opened_at)) : null} active={emailOpens > 0} count={emailOpens} />
        <TimelineRow icon={MousePointer} label="Link Clicked" time={linkClicks > 0 ? (linkClicks + 'x — Last: ' + fmtDate(log ? log.last_viewed_at : null)) : null} active={linkClicks > 0} count={linkClicks} />
        <TimelineRow icon={FileText} label="Quote Signed" time={isSigned ? fmtDate(quote.signed_at) : null} active={!!isSigned} />
        <TimelineRow icon={CreditCard} label="Payment" time={quote.deposit_paid ? 'Initial Payment Received' : null} active={!!quote.deposit_paid} />
      </div>

      {/* Analytics Summary */}
      <div className="grid grid-cols-3 gap-3 mt-4">
        <StatBox label="Email Opens" value={emailOpens} color={emailOpens > 0 ? 'text-green-600' : 'text-gray-400'} />
        <StatBox label="Link Clicks" value={linkClicks} color={linkClicks > 0 ? 'text-blue-600' : 'text-gray-400'} />
        <StatBox label="View Details" value={views.length + ' visits'} color="text-gray-600" />
      </div>

      {/* Recent Views */}
      {views.length > 0 && (
        <div className="mt-4 pt-3 border-t">
          <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Recent Views</p>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {views.slice(0, 10).map(function(v, i) {
              return (
                <div key={i} className="flex items-center justify-between text-xs text-gray-500 py-1">
                  <span className="flex items-center gap-2"><Eye className="w-3 h-3" /> {fmtDate(v.viewed_at)}</span>
                  <span className="text-gray-400">{v.ip}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function TimelineRow({ icon: Icon, label, time, active, count }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="flex flex-col items-center">
        <div className={'w-7 h-7 rounded-full flex items-center justify-center ' + (active ? 'bg-green-100' : 'bg-gray-100')}>
          <Icon className={'w-3.5 h-3.5 ' + (active ? 'text-green-600' : 'text-gray-400')} />
        </div>
      </div>
      <div className="flex-1">
        <p className={'text-sm font-medium ' + (active ? 'text-[#0A1628]' : 'text-gray-400')}>{label}</p>
      </div>
      <div className="text-right">
        {active ? (
          <p className="text-xs text-green-600 font-medium">{time}</p>
        ) : (
          <p className="text-xs text-gray-300">Waiting...</p>
        )}
      </div>
    </div>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3 text-center">
      <p className="text-[10px] text-gray-400 uppercase">{label}</p>
      <p className={'text-lg font-bold ' + color}>{value}</p>
    </div>
  );
}
